import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, change, reduxForm } from 'redux-form';
import { Button, Col, Modal, ModalBody, ModalHeader, Row, Card, CardBody } from 'reactstrap';
import { connect } from 'react-redux';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonIcon from '@mui/icons-material/Person';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import PinDropIcon from '@mui/icons-material/PinDrop';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import { GRL_URL } from 'src/utils/pnd/PndConstants';
import { isNumber, mobileNoFormat, mobileNoLength, validateUaeLandline } from 'src/utils/validationUtils';
import renderField from 'src/shared/components/form/renderField';

const required = value => (value ? undefined : 'Required');

const PickUpPointsAddOrUpdate = ({
  handleSubmit,
  setMapData,
  setLatitude,
  setLongitide,
  address,
  dataInfo,
  dispatch,
  onCancel,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(address || dataInfo?.collectionPointAddress || '');
  const [apaIframeUrl] = useState(GRL_URL);
  const apaFrameRef = useRef(null);

  useEffect(() => {
    if (address || dataInfo?.collectionPointAddress) {
      setCurrentAddress(address || dataInfo?.collectionPointAddress || '');
    }
  }, [address, dataInfo]);

  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const handleFindLocation = () => {
    toggleModal();
    handleMessageToIframe();
  };

  const handleMessageToIframe = () => {
    const datajson =
      '{"RequestID": ' +
      Math.floor(Math.random() * 20 + 1) +
      ',"MessageType":1,"AddressElements": [{"AddressElementTypeCode": 1,"Value":""},{"AddressElementTypeCode":2,"Value": ""},{ "AddressElementTypeCode": 3,"Value": ""},{"AddressElementTypeCode":4,"Value": ""},{"AddressElementTypeCode": 5,"Value":"520950"},{"AddressElementTypeCode": 6,"Value":"" },{"AddressElementTypeCode": 7, "Value": "" },{"AddressElementTypeCode": 8,"Value":""}]}';
    if (apaFrameRef.current) {
      apaFrameRef.current.contentWindow.postMessage(datajson, '*');
      window.addEventListener('message', handleMessageFromIframe);
    } else {
      console.error('iframe element not found');
    }
  };

  const handleMessageFromIframe = event => {
    if (event && event?.data) {
      try {
        const jsonRegex = /^\s*(\{.*\}|\[.*\])\s*$/;
        if (jsonRegex.test(event?.data)) {
          const data_from_apa = JSON.parse(event.data);
          const buildingName = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 6,
          );
          const landmark = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 4,
          );
          const street = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 3,
          );
          const area = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 2,
          );
          const zone = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 7,
          );
          const city = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 8,
          );
          const emirates = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 1,
          );
          const latitude = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 10,
          );
          const longitude = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 9,
          );
          const grl = data_from_apa.AddressElements.find(
            element => element.AddressElementTypeCode === 5,
          );

          let deliveryAddress = '';
          if (buildingName && buildingName?.Value !== '') {
            deliveryAddress = deliveryAddress + buildingName.Value + ',';
          }
          if (buildingName?.Value === '' && landmark?.Value !== '') {
            deliveryAddress = deliveryAddress + landmark.Value + ',';
          }
          if (street && street?.Value !== '') {
            deliveryAddress = deliveryAddress + street.Value + ',';
          }
          if (area && area?.Value !== '') {
            deliveryAddress = deliveryAddress + area.Value + ',';
          }
          if (zone && zone?.Value !== '') {
            deliveryAddress = deliveryAddress + zone.Value + ',';
          }
          if (city && city?.Value !== '') {
            deliveryAddress = deliveryAddress + city.Value + ',';
          }
          if (emirates && emirates?.Value !== '') {
            deliveryAddress = deliveryAddress + emirates.Value;
          }

          const cleanAddress = deliveryAddress.replace(/,+/g, ',').replace(/^,|,$/g, '');

          if (cleanAddress) {
            setCurrentAddress(cleanAddress);
            if (setMapData) {
              setMapData(cleanAddress);
            }
            if (dispatch) {
              dispatch(change('PickUpPointsAddOrUpdateForm', 'collectionPointAddress', cleanAddress));
              dispatch(change('PickUpPointsAddOrUpdateForm', 'address', cleanAddress));
            }
          }
          if (latitude && setLatitude) {
            setLatitude(latitude.Value);
            if (dispatch) {
              dispatch(change('PickUpPointsAddOrUpdateForm', 'latitude', latitude.Value));
            }
          }
          if (longitude && setLongitide) {
            setLongitide(longitude.Value);
            if (dispatch) {
              dispatch(change('PickUpPointsAddOrUpdateForm', 'longitude', longitude.Value));
            }
          }
          if (grl && dispatch) {
            dispatch(change('PickUpPointsAddOrUpdateForm', 'collectionPointGrl', grl.Value));
          }
          toggleModal();
        }
      } catch (error) {
        console.error('event.data is not a valid JSON string');
      }
    }
  };

  const handleAddressInputChange = e => {
    const val = e.target.value;
    setCurrentAddress(val);
    if (setMapData) {
      setMapData(val);
    }
    if (dispatch) {
      dispatch(change('PickUpPointsAddOrUpdateForm', 'collectionPointAddress', val));
      dispatch(change('PickUpPointsAddOrUpdateForm', 'address', val));
    }
  };

  return (
    <form className="p-3 bg-light" onSubmit={handleSubmit}>
      <Col lg={12} md={12}>
        {/* Section 1: Location Information */}
        <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '12px' }}>
          <CardBody className="p-3">
            <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
              <StorefrontIcon style={{ fontSize: '1.25rem', color: '#32CA73' }} className="me-2" />
              <h6 className="m-0 fw-bold small text-uppercase" style={{ color: '#1e293b', letterSpacing: '0.5px' }}>
                Location Information
              </h6>
            </div>
            <Row className="g-2">
              <Col lg={6} md={6}>
                <div className="mb-1">
                  <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                    Pick Up Location Name <span className="text-danger">*</span>
                  </label>
                  <Field
                    name="collectionPointName"
                    component={renderField}
                    type="text"
                    placeholder="Enter Location Name (e.g. Dubai Central Depot)"
                    validate={[required]}
                    icon={StorefrontIcon}
                  />
                </div>
              </Col>
              <Col lg={6} md={6}>
                <div className="mb-1">
                  <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                    Pick Up Point Code <span className="text-danger">*</span>
                  </label>
                  <Field
                    name="collectionPointCode"
                    component={renderField}
                    type="text"
                    placeholder="Enter Code (e.g. PKP-001)"
                    validate={[required]}
                    icon={BadgeIcon}
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Section 2: Contact Details */}
        <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '12px' }}>
          <CardBody className="p-3">
            <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
              <PersonIcon style={{ fontSize: '1.25rem', color: '#0284c7' }} className="me-2" />
              <h6 className="m-0 fw-bold small text-uppercase" style={{ color: '#1e293b', letterSpacing: '0.5px' }}>
                Contact Information
              </h6>
            </div>
            <Row className="g-2 mb-2">
              <Col lg={6} md={6}>
                <div className="mb-1">
                  <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                    Contact Person Name
                  </label>
                  <Field
                    name="collectionPointContactName"
                    component={renderField}
                    type="text"
                    placeholder="Enter Contact Person"
                    icon={PersonIcon}
                  />
                </div>
              </Col>
              <Col lg={6} md={6}>
                <div className="mb-1">
                  <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                    Contact Mobile Number <span className="text-danger">*</span>
                  </label>
                  <Field
                    name="collectionPointContactMobileNo"
                    component={renderField}
                    type="text"
                    placeholder="971501234567"
                    validate={[required, mobileNoFormat, mobileNoLength]}
                    icon={SmartphoneIcon}
                  />
                </div>
              </Col>
            </Row>
            <Row className="g-2">
              <Col lg={6} md={6}>
                <div className="mb-1">
                  <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                    Telephone Number
                  </label>
                  <Field
                    name="collectionPointPhoneNo"
                    component={renderField}
                    type="text"
                    placeholder="04xxxxxxx"
                    validate={[isNumber, validateUaeLandline]}
                    icon={LocalPhoneIcon}
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Section 3: Address & Location Mapping */}
        <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '12px' }}>
          <CardBody className="p-3">
            <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
              <LocationOnIcon style={{ fontSize: '1.25rem', color: '#f59e0b' }} className="me-2" />
              <h6 className="m-0 fw-bold small text-uppercase" style={{ color: '#1e293b', letterSpacing: '0.5px' }}>
                Address & Location Mapping
              </h6>
            </div>
            <Row className="g-2 mb-2">
              <Col md={12}>
                <div className="mb-1">
                  <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                    Pick Up Point Address <span className="text-danger">*</span>
                  </label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-end-0 text-muted" style={{ padding: '0.35rem 0.65rem' }}>
                      <LocationOnIcon style={{ fontSize: '1.1rem', color: '#ef4444' }} />
                    </span>
                    <input
                      className="form-control form-control-sm border-start-0"
                      name="address"
                      type="text"
                      value={currentAddress}
                      onChange={handleAddressInputChange}
                      placeholder="Enter address or click Find Location on Map"
                      style={{ fontSize: '0.85rem' }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-success d-flex align-items-center px-3"
                      onClick={handleFindLocation}
                      style={{
                        background: 'linear-gradient(135deg, #32CA73 0%, #21b368 100%)',
                        border: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                      }}
                    >
                      <AddLocationAltIcon style={{ fontSize: '1.05rem' }} className="me-1" />
                      Find Location
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="g-2">
              <Col lg={6} md={6}>
                <div className="mb-1">
                  <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                    Pick Up Point GRL
                  </label>
                  <Field
                    name="collectionPointGrl"
                    component={renderField}
                    type="text"
                    placeholder="Auto-populated from map"
                    disabled={true}
                    icon={PinDropIcon}
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>

      {/* Form Action Buttons */}
      <div className="d-flex justify-content-end align-items-center pt-2 mt-1 border-top gap-2">
        {onCancel && (
          <Button
            type="button"
            color="secondary"
            outline
            size="sm"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-pill"
            style={{ fontWeight: 600, fontSize: '0.85rem' }}
          >
            <CloseIcon style={{ fontSize: '1rem' }} className="me-1" />
            Cancel
          </Button>
        )}
        <Button
          color="primary"
          type="submit"
          size="sm"
          className="px-4 py-1.5 rounded-pill shadow-sm d-flex align-items-center"
          style={{
            background: 'linear-gradient(135deg, #32CA73 0%, #21b368 100%)',
            border: 'none',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.875rem',
            boxShadow: '0 4px 12px rgba(50, 202, 115, 0.35)',
          }}
        >
          <SaveIcon style={{ fontSize: '1.05rem' }} className="me-1" />
          {dataInfo ? 'Update Details' : 'Submit Details'}
        </Button>
      </div>

      {/* Find Location Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl" style={{ maxWidth: '1000px' }}>
        <ModalHeader toggle={toggleModal}>
          <div className="d-flex align-items-center">
            <LocationOnIcon className="me-2 text-success" style={{ fontSize: '1.5rem' }} />
            <span className="fw-bold">Find Location on Map</span>
          </div>
        </ModalHeader>
        <ModalBody className="p-0">
          <iframe
            ref={apaFrameRef}
            src={apaIframeUrl}
            width="100%"
            height="620px"
            title="Get Address"
            onLoad={handleMessageToIframe}
            style={{ border: 'none', width: '100%' }}
          />
        </ModalBody>
      </Modal>
    </form>
  );
};

PickUpPointsAddOrUpdate.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  setMapData: PropTypes.func,
  setLatitude: PropTypes.func,
  setLongitide: PropTypes.func,
  address: PropTypes.string,
  dataInfo: PropTypes.shape(),
  dispatch: PropTypes.func,
  onCancel: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
  if (ownProps && ownProps?.dataInfo) {
    const info = ownProps.dataInfo;
    return {
      initialValues: {
        pickUpPointPointId: info?.pickUpPointPointId,
        collectionPointName: info?.collectionPointName,
        collectionPointCode: info?.collectionPointCode,
        collectionPointContactName: info?.collectionPointContactName,
        collectionPointContactMobileNo: info?.collectionPointContactMobileNo,
        collectionPointPhoneNo: info?.collectionPointPhoneNo,
        collectionPointAddress: info?.collectionPointAddress,
        address: info?.collectionPointAddress,
        collectionPointGrl: info?.collectionPointGrl,
        latitude: info?.latitude,
        longitude: info?.longitude,
      },
    };
  }
  return {};
};

export default connect(mapStateToProps)(
  reduxForm({ form: 'PickUpPointsAddOrUpdateForm', enableReinitialize: true })(PickUpPointsAddOrUpdate),
);